import * as Yup from 'yup'
import 'yup-phone-lite'
import 'src/validators/israeli-id-validator'
import {StipendCalendarType} from 'src/enum/stipendCalendarType'
import {IndificationType} from 'src/enum/indificationType'

/* Regex */
const lettersRegex = /^[A-Za-z\u0590-\u05FF \-']+$/
const passportRe = /^[a-zA-Z0-9]{6,20}$/
const numberRegex = /^\d*$/
const emailRe = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/

const contactDetails = {
  firstName: Yup.string()
    .nullable()
    .trim()
    .min(2, 'שם פרטי חייב להכיל לפחות שני תווים')
    .required('שם פרטי הוא שדה חובה')
    .matches(lettersRegex, 'שם פרטי יכול להכיל רק אותיות'),
  lastName: Yup.string()
    .nullable()
    .trim()
    .min(2, 'שם משפחה חייב להכיל לפחות שני תווים')
    .matches(lettersRegex, 'שם משפחה יכול להכיל רק אותיות')
    .required('שם משפחה הוא שדה חובה'),
  indificationTypeID: Yup.number().required('סוג הזדהות הוא שדה חובה'),
  indificationNo: Yup.string().when('indificationTypeID', {
    is: IndificationType.IndificationNo,
    then: schema =>
      schema
        .required('תעודת זהות היא שדה חובה')
        .israeliIdentity('מספר תעודת זהות לא תקין'),
    otherwise: schema => schema.notRequired()
  }),
  passport: Yup.string().when('indificationTypeID', {
    is: IndificationType.Passport,
    then: schema =>
      schema
        .required('דרכון הוא שדה חובה')
        .matches(passportRe, 'מספר דרכון לא תקין'),
    otherwise: schema => schema.notRequired()
  }),
  title: Yup.string().required('תפקיד הוא שדה חובה'),
  phone: Yup.string()
    .required('טלפון הוא שדה חובה')
    .phone('IL', 'מספר טלפון לא תקין'),
  cellPhone: Yup.string()
    .required('טלפון נייד הוא שדה חובה')
    .phone('IL', 'מספר טלפון נייד לא תקין'),
  email: Yup.string()
    .required('מייל הוא שדה חובה')
    .matches(emailRe, 'כתובת דוא"ל לא תקינה'),
  street: Yup.string().required('רחוב הוא שדה חובה'),
  houseNumber: Yup.string()
    .matches(numberRegex, "מס' בית לא תקין")
    .required('מס בית הוא שדה חובה'),
  zipCode: Yup.string().matches(numberRegex, 'מיקוד לא תקין'),
  city: Yup.string().required('עיר הוא שדה חובה')
}

const institutionDetails = {
  name: Yup.string().required('שם מוסד הוא שדה חובה'),
  institutionHeadName: Yup.string().required(
    'שם ראש כולל/ראש ישיבה הוא שדה חובה'
  ),
  phone: Yup.string()
    .required('טלפון הוא שדה חובה')
    .phone('IL', 'מספר טלפון לא תקין'),
  institutionHeadPhone: Yup.string()
    .required('טלפון ראש כולל/ראש ישיבה הוא שדה חובה')
    .phone('IL', 'מספר טלפון לא תקין'),
  street: Yup.string().required('כתובת היא שדה חובה'),
  houseNumber: Yup.string()
    .matches(numberRegex, 'מס’ בית לא תקין')
    .required('מס’ בית הוא שדה חובה'),
  zipCode: Yup.string().matches(numberRegex, 'מיקוד לא תקין'),
  city: Yup.string().required('עיר הוא שדה חובה'),
  isCommitmentConfirmation: Yup.boolean()
    .oneOf([true], 'שדה חובה')
    .required('שדה חובה'),
  signature: Yup.string().required('יש לחתום על הטופס'),
  isAssociationBelongTo: Yup.boolean(),
  externalNote: Yup.string(),
  stipendCalendarType: Yup.number().required('תשלום חודשי לפי הוא שדה חובה'),
  stipendPaymentDay: Yup.number()
    .transform((v, orig) => (orig === '' || orig === null ? undefined : v))
    .typeError('יום התשלום הוא שדה חובה')
    .required('יום התשלום הוא שדה חובה')
    .when('stipendCalendarType', {
      is: StipendCalendarType.Gregorian,
      then: schema =>
        schema.min(1, 'יש להזין יום בין 1-31').max(31, 'יש להזין יום בין 1-31'),
      otherwise: schema => schema.min(1, 'יש לבחור יום').max(30, 'יש לבחור יום')
    }),
  receivesDatotBudget: Yup.boolean()
}

const associationDetails = {
  name: Yup.string().required('שם עמותה הוא שדה חובה'),
  associationId: Yup.string()
    .matches(numberRegex, 'מספר עמותה לא תקין')
    .required('מספר עמותה הוא שדה חובה'),
  street: Yup.string().required('כתובת היא שדה חובה'),
  houseNumber: Yup.string()
    .matches(numberRegex, 'מס’ בית לא תקין')
    .required('מספר בית הוא שדה חובה'),
  zipCode: Yup.string().matches(numberRegex, 'מיקוד לא תקין'),
  city: Yup.string().required('עיר הוא שדה חובה'),
  phone: Yup.string()
    .required('טלפון הוא שדה חובה')
    .phone('IL', 'מספר טלפון לא תקין'),
  accountOwner: Yup.string().required('שם בעל החשבון הוא שדה חובה'),
  bank: Yup.string().required('בנק הוא שדה חובה'),
  branch: Yup.string().required('מספר סניף הוא שדה חובה'),
  bankId: Yup.number(),
  branchId: Yup.number(),
  accountNumber: Yup.string()
    .matches(numberRegex, 'מספר חשבון לא תקין')
    .required('מספר חשבון הוא שדה חובה'),
  swiftCode: Yup.string()
}

const schema = Yup.object({
  ...institutionDetails,
  contact: Yup.object().shape(contactDetails),
  association: Yup.object().shape(associationDetails)
})

export default schema
